import React from 'react';
import {
    Box,
    Button,
    Card,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Select,
    MenuItem,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    CheckCircle,
    Schedule,
} from '@mui/icons-material';
import { useCanAccess } from 'react-admin';
import { ProjectFlowPhase } from '@/types';
import {
    stepStatusLabel,
    nextStatus,
    formatFlowDate,
} from './projectFlowUtils';
import {
    useCreateFlowStep,
    useUpdateFlowStep,
    useDeleteFlowStep,
} from './useProjectFlow';
import { StepComments } from './StepComments';
import { CommentComposer } from './CommentComposer';

interface ContainerPhasePanelProps {
    projectId: number;
    phase: ProjectFlowPhase;
    currentUserId?: number;
}

export const ContainerPhasePanel: React.FC<ContainerPhasePanelProps> = ({
    projectId,
    phase,
    currentUserId,
}) => {
    const [addDialogOpen, setAddDialogOpen] = React.useState(false);
    const [editingStepId, setEditingStepId] = React.useState<number | null>(null);
    const [expandedStepId, setExpandedStepId] = React.useState<number | null>(null);
    const [newEntryName, setNewEntryName] = React.useState('');
    const { canAccess: canUpdate } = useCanAccess({
        resource: 'project_flow',
        action: 'update',
    });
    const createStepMutation = useCreateFlowStep();
    const updateStepMutation = useUpdateFlowStep();
    const deleteStepMutation = useDeleteFlowStep();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const handleAddEntry = async () => {
        if (!newEntryName.trim()) return;

        createStepMutation.mutate(
            {
                projectId,
                data: {
                    phase: phase.key,
                    name: newEntryName.trim(),
                },
            },
            {
                onSuccess: () => {
                    setNewEntryName('');
                    setAddDialogOpen(false);
                },
            }
        );
    };

    const handleStatusChange = (stepId: number, newStatus: string) => {
        updateStepMutation.mutate({
            projectId,
            stepId,
            data: { status: newStatus },
        });
    };

    const handleRenameEntry = (stepId: number, newName: string) => {
        if (!newName.trim()) return;
        updateStepMutation.mutate({
            projectId,
            stepId,
            data: { name: newName.trim() },
        });
        setEditingStepId(null);
    };

    const handleDeleteEntry = (stepId: number) => {
        if (!window.confirm('Delete this entry?')) return;
        deleteStepMutation.mutate({
            projectId,
            stepId,
        });
    };

    return (
        <Stack gap={2} sx={{ p: 2 }}>
            {phase.steps.length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
                    No {phase.label.toLowerCase()} entries yet
                </Typography>
            )}

            {phase.steps.map((step) => (
                <Card key={step.id} sx={{ p: 2 }}>
                    <Stack gap={2}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            gap={1}
                        >
                            <Stack gap={1} flex={1}>
                                {editingStepId === step.id ? (
                                    <TextField
                                        size="small"
                                        fullWidth
                                        defaultValue={step.name}
                                        autoFocus
                                        onBlur={(e) =>
                                            handleRenameEntry(
                                                step.id,
                                                e.target.value
                                            )
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleRenameEntry(
                                                    step.id,
                                                    (
                                                        e.target as HTMLInputElement
                                                    ).value
                                                );
                                            }
                                        }}
                                        disabled={updateStepMutation.isPending}
                                    />
                                ) : (
                                    <>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight="bold"
                                        >
                                            {step.name}
                                        </Typography>
                                        {step.completed_at && (
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                            >
                                                Completed:{' '}
                                                {formatFlowDate(
                                                    step.completed_at
                                                )}
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </Stack>

                            <Stack
                                direction="row"
                                gap={1}
                                alignItems="center"
                                sx={{ ml: 'auto' }}
                            >
                                {isSmallScreen ? (
                                    <Select
                                        size="small"
                                        value={step.status}
                                        onChange={(e) =>
                                            handleStatusChange(
                                                step.id,
                                                e.target.value
                                            )
                                        }
                                        disabled={
                                            !canUpdate ||
                                            updateStepMutation.isPending
                                        }
                                        sx={{ width: 120 }}
                                    >
                                        <MenuItem value="pending">Pending</MenuItem>
                                        <MenuItem value="in_progress">
                                            In Progress
                                        </MenuItem>
                                        <MenuItem value="done">Done</MenuItem>
                                    </Select>
                                ) : (
                                    <Button
                                        size="small"
                                        onClick={() =>
                                            handleStatusChange(
                                                step.id,
                                                nextStatus(step.status as any)
                                            )
                                        }
                                        disabled={
                                            !canUpdate ||
                                            updateStepMutation.isPending
                                        }
                                    >
                                        {stepStatusLabel(step.status as any)}
                                    </Button>
                                )}

                                {canUpdate && (
                                    <>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setEditingStepId(
                                                    editingStepId === step.id
                                                        ? null
                                                        : step.id
                                                )
                                            }
                                            disabled={
                                                updateStepMutation.isPending
                                            }
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                handleDeleteEntry(step.id)
                                            }
                                            disabled={
                                                deleteStepMutation.isPending
                                            }
                                        >
                                            {deleteStepMutation.isPending &&
                                            deleteStepMutation.variables?.stepId ===
                                                step.id ? (
                                                <CircularProgress size={16} />
                                            ) : (
                                                <Delete fontSize="small" />
                                            )}
                                        </IconButton>
                                    </>
                                )}
                            </Stack>
                        </Stack>

                        {/* Comments section */}
                        {step.comment_count > 0 && (
                            <Box
                                sx={{
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    pt: 2,
                                }}
                            >
                                <StepComments
                                    projectId={projectId}
                                    stepId={step.id}
                                    comments={step.comments || []}
                                    totalCount={step.comment_count}
                                    currentUserId={currentUserId}
                                />
                            </Box>
                        )}

                        <Box
                            sx={{
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                pt: 2,
                            }}
                        >
                            <CommentComposer
                                projectId={projectId}
                                stepId={step.id}
                            />
                        </Box>
                    </Stack>
                </Card>
            ))}

            {canUpdate && (
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setAddDialogOpen(true)}
                    disabled={createStepMutation.isPending}
                >
                    Add Entry
                </Button>
            )}

            <Dialog
                open={addDialogOpen}
                onClose={() => {
                    setAddDialogOpen(false);
                    setNewEntryName('');
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add {phase.label} Entry</DialogTitle>
                <DialogContent>
                    <Stack gap={2} sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Entry name"
                            placeholder="e.g., Leak call – Mar 2027"
                            value={newEntryName}
                            onChange={(e) => setNewEntryName(e.target.value)}
                            disabled={createStepMutation.isPending}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleAddEntry();
                                }
                            }}
                        />
                        <Stack direction="row" gap={1} justifyContent="flex-end">
                            <Button
                                onClick={() => {
                                    setAddDialogOpen(false);
                                    setNewEntryName('');
                                }}
                                disabled={createStepMutation.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleAddEntry}
                                disabled={
                                    !newEntryName.trim() ||
                                    createStepMutation.isPending
                                }
                            >
                                Add Entry
                            </Button>
                        </Stack>
                    </Stack>
                </DialogContent>
            </Dialog>
        </Stack>
    );
};

ContainerPhasePanel.displayName = 'ContainerPhasePanel';
